import { AxiosRequestConfig } from 'axios';
import { logger, sanitize } from './logger';
import { DateTime } from 'luxon';
import { makeRequest } from './requestService';
import config from '@config/config';

export type TokenDTO = {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  refresh_token: string;
  issued_at: number;
};

export type getAccessTokenPayload = {
  memberId?: string;
  tokenType?: 'ACCESS_TOKEN' | 'ADMIN_TOKEN';
};

export type fetchAccessTokenPayload = {
  memberId?: string;
  tokenType?: 'ACCESS_TOKEN' | 'ADMIN_TOKEN';
};

let accessTokenResponse: TokenDTO;
let adminAccessTokenResponse: TokenDTO;

export const getAccessToken = async ({ memberId, tokenType }: getAccessTokenPayload) => {
  if (tokenType === 'ACCESS_TOKEN' && accessTokenResponse?.expires_in) {
    const now = DateTime.now().toSeconds();
    const isTokenExpired =
      now >= accessTokenResponse.issued_at + accessTokenResponse.expires_in - 500;
    if (!isTokenExpired) {
      return accessTokenResponse.access_token;
    }
  }

  if (tokenType === 'ADMIN_TOKEN' && adminAccessTokenResponse?.expires_in) {
    const now = DateTime.now().toSeconds();
    const isTokenExpired =
      now >= adminAccessTokenResponse.issued_at + adminAccessTokenResponse.expires_in - 500;
    if (!isTokenExpired) {
      return adminAccessTokenResponse.access_token;
    }
  }

  return fetchAccessToken({ memberId, tokenType });
};

export const fetchAccessToken = async ({ memberId, tokenType }: fetchAccessTokenPayload) => {
  const params = new URLSearchParams();
  params.append('scope', 'partner offline_access');
  params.append('grant_type', 'client_credentials_on_behalf_of');
  params.append('client_id', 'groov.integrations.teams');
  params.append('client_secret', config.clientSecret!);
  if (tokenType === 'ACCESS_TOKEN' && memberId) {
    params.append('partner_member_id', memberId);
  }

  if (tokenType === 'ADMIN_TOKEN') {
    params.append('partner_member_id', '2ed6d43c-c65d-4472-8ff3-c63107919301');
  }

  const axiosConfig: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    url: config.loginEndpoint,
    method: 'POST',
    data: params,
  };

  try {
    const data = await makeRequest(axiosConfig);
    if (data?.access_token) {
      const tokenResponse: TokenDTO = {
        ...data,
        issued_at: DateTime.now().toSeconds(),
      };

      if (tokenType === 'ACCESS_TOKEN') {
        accessTokenResponse = tokenResponse;
      }
      if (tokenType === 'ADMIN_TOKEN') {
        adminAccessTokenResponse = tokenResponse;
      }
      logger.debug(
        'Token Service -> fetchAccessToken',
        `New token expiry is: ${DateTime.fromSeconds(
          tokenResponse.issued_at + tokenResponse.expires_in
        ).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}`
      );

      return tokenResponse.access_token;
    }
  } catch (error) {
    logger.error(
      'Token Service',
      'fetchAccessToken',
      `Error fetching access token -> ${JSON.stringify(sanitize(axiosConfig))}`,
      error
    );
    throw error;
  }
};
